/**
 * §P.7 · Qué disciplinas imparte el club.
 *
 * ## El `PUT` sustituye la lista entera
 *
 * Y eso cambia por completo cómo tiene que estar hecha esta pantalla: **quitar una
 * es tan fácil como olvidarse de mandarla**. Un formulario que mandara «lo que he
 * tocado» borraría en silencio las que no tocó, así que aquí se marca sobre la
 * lista completa de las cuatro y se manda entera, siempre.
 *
 * Por lo mismo no hay un botón de «quitar» por fila: quitar es desmarcar, y lo que
 * se guarda es el estado final que se está viendo.
 *
 * ## Una que se sigue impartiendo no se quita
 *
 * El servidor responde `409 discipline_in_use` con los nombres de los grupos
 * dentro. La pantalla no espera a ese error para decirlo: si una disciplina tiene
 * grupos, lo dice en su fila y desmarcarla avisa **antes** de guardar. El error del
 * servidor sigue siendo la verdad —alguien pudo crear un grupo mientras tanto— pero
 * enterarse al pulsar «Guardar» es enterarse tarde.
 *
 * ## El sistema de grados va aquí y no en §P.5
 *
 * Porque es lo que ata la disciplina con su escalera de cinturones: sin él, un
 * alumno de Karate no tiene a qué grado subir. Se elige entre los del club y las
 * plantillas globales de esa disciplina, que es lo que devuelve
 * `GET /clubs/{club}/belt-schemes`.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import type { SistemaDeGrados } from '@/club/grados/tipos';
import type { DisciplinaDelClub } from '@/club/ajustes/tipos';

interface DisciplinaGlobal {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

/** Lo elegido: disciplina → sistema de grados (o `null`, que es «todavía ninguno»). */
type Elegidas = ReadonlyMap<string, string | null>;

export function Disciplinas({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [elegidas, setElegidas] = useState<Elegidas>(() => new Map());
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);

  // El catálogo global: las cuatro. No depende del club.
  const catalogo = useQuery({
    queryKey: ['disciplinas'],
    queryFn: async () => {
      const { data, error } = await api.v1.disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DisciplinaGlobal[];
    },
  });

  const delClub = useQuery({
    queryKey: ['disciplinas-del-club', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DisciplinaDelClub[];
    },
  });

  const sistemas = useQuery({
    queryKey: ['sistemas-de-grados', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['belt-schemes'].get({
        /*
         * **Con las plantillas globales**, al revés que la lista de §P.5, donde son
         * ruido porque son de todos y no de este club.
         *
         * Aquí hacen falta: `schemesAvailable` acepta `academy_id IS NULL`, así que
         * una disciplina puede apuntar directamente a la plantilla. Sin esto, un
         * club recién dado de alta tiene el desplegable vacío y no puede decir por
         * qué escalera suben sus alumnos hasta que clone un sistema — que es pedirle
         * que entienda la diferencia antes de haber usado ninguna de las dos.
         */
        query: { include_templates: true },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SistemaDeGrados[];
    },
  });

  // Se siembra durante el render, no desde un efecto: con un efecto se pinta una
  // vez con todo desmarcado, que es exactamente lo que esta pantalla no debe
  // enseñar nunca.
  if (delClub.data && sembradoDe !== club) {
    setSembradoDe(club);
    setElegidas(
      new Map(delClub.data.map((d) => [d.discipline_id, d.belt_scheme_id]))
    );
  }

  const guardar = useMutation({
    mutationFn: async (e: Elegidas) => {
      const r = await api.v1.clubs({ club }).disciplines.put({
        // La lista entera, siempre. Ver la cabecera.
        disciplines: [...e].map(([discipline_id, belt_scheme_id]) => ({
          discipline_id,
          belt_scheme_id,
        })),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setGuardado(true);
      void cache.invalidateQueries({
        queryKey: ['disciplinas-del-club', club],
      });
      void cache.invalidateQueries({ queryKey: ['sistemas-de-grados', club] });
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  if (catalogo.isPending || delClub.isPending || sistemas.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las disciplinas" />;
  }

  const fallo = catalogo.error ?? delClub.error ?? sistemas.error;
  if (fallo || !catalogo.data || !delClub.data || !sistemas.data) {
    return (
      <PanelError
        problema={aProblema(fallo)}
        reintentar={() => {
          void catalogo.refetch();
          void delClub.refetch();
          void sistemas.refetch();
        }}
      />
    );
  }

  const actual = new Map(delClub.data.map((d) => [d.discipline_id, d]));

  const marcar = (id: string, marcada: boolean) => {
    setGuardado(false);
    setElegidas((e) => {
      const n = new Map(e);
      if (marcada) n.set(id, n.get(id) ?? null);
      else n.delete(id);
      return n;
    });
  };

  const elegirSistema = (id: string, schemeId: string) => {
    setGuardado(false);
    setElegidas((e) => new Map(e).set(id, schemeId === '' ? null : schemeId));
  };

  // Las que se van a quitar y siguen dándose. El servidor lo rechaza igual; esto
  // es para que no se descubra al pulsar «Guardar».
  const quitandoEnUso = delClub.data.filter(
    (d) => !elegidas.has(d.discipline_id) && d.group_count > 0
  );
  const ninguna = elegidas.size === 0;

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate(elegidas);
      }}
    >
      <h1>Disciplinas</h1>

      <p className="tenue">
        Lo que se enseña en el club. Cada una con su sistema de grados, que es
        la escalera de cinturones por la que suben sus alumnos.
      </p>

      <ul className="disciplinas">
        {catalogo.data.map((d) => {
          const marcada = elegidas.has(d.id);
          const suya = actual.get(d.id);
          const grupos = suya?.group_count ?? 0;
          const suyos = sistemas.data.filter((s) => s.discipline_id === d.id);

          return (
            <li key={d.id} className={marcada ? undefined : 'fila-fuera'}>
              <label className="opcion">
                <input
                  type="checkbox"
                  checked={marcada}
                  disabled={!puedeEditar}
                  onChange={(ev) => marcar(d.id, ev.target.checked)}
                />
                <strong>{d.name}</strong>
              </label>

              {grupos > 0 && (
                <p className="tenue">
                  {grupos === 1
                    ? 'La imparte 1 grupo activo.'
                    : `La imparten ${grupos} grupos activos.`}
                </p>
              )}

              {marcada && (
                <label>
                  Sistema de grados
                  <select
                    value={elegidas.get(d.id) ?? ''}
                    disabled={!puedeEditar}
                    onChange={(ev) => elegirSistema(d.id, ev.target.value)}
                  >
                    <option value="">Sin sistema de grados todavía</option>
                    {suyos.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ·{' '}
                        {s.academy_id === null
                          ? 'plantilla de Kiap'
                          : 'del club'}{' '}
                        · {s.rank_count} cinturones
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {marcada && suyos.length === 0 && (
                <p className="tenue">
                  No hay ningún sistema de grados para {d.name}. Se crea desde
                  Grados; hasta entonces sus alumnos no tienen a qué cinturón
                  subir.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        El aviso de lo que se está quitando, con los nombres. Desmarcar una casilla
        no se parece a borrar nada, y aquí sí lo es.
      */}
      {quitandoEnUso.length > 0 && (
        <div className="aviso" role="status">
          {quitandoEnUso.length === 1 ? (
            <>
              <strong>
                Estás quitando {quitandoEnUso[0]!.name}, y se sigue impartiendo.
              </strong>{' '}
              {quitandoEnUso[0]!.group_count === 1
                ? 'Hay un grupo activo dándola'
                : `Hay ${quitandoEnUso[0]!.group_count} grupos activos dándola`}
              , así que el servidor no va a dejar guardarlo. Cambia ese grupo de
              disciplina o ciérralo primero.
            </>
          ) : (
            <>
              <strong>
                Estás quitando {quitandoEnUso.length} disciplinas que se siguen
                impartiendo.
              </strong>{' '}
              Hay grupos activos dando{' '}
              {quitandoEnUso.map((d) => d.name).join(', ')}, así que el servidor
              no va a dejar guardarlo. Cambia esos grupos de disciplina o
              ciérralos primero.
            </>
          )}
        </div>
      )}

      {ninguna && (
        <div className="aviso" role="status">
          <strong>Un club imparte al menos una disciplina.</strong> Sin ninguna
          no se pueden crear grupos ni graduar a nadie.
        </div>
      )}

      {problema && <PanelError problema={problema} />}

      {guardado && !guardar.isPending && (
        <div className="aviso aviso-calma" role="status">
          <strong>Guardado.</strong>
        </div>
      )}

      {puedeEditar && (
        <div className="acciones">
          <button
            type="submit"
            disabled={guardar.isPending || ninguna || quitandoEnUso.length > 0}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar las disciplinas'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setElegidas(
                new Map(
                  delClub.data.map((d) => [d.discipline_id, d.belt_scheme_id])
                )
              );
              setProblema(null);
              setGuardado(false);
            }}
          >
            Deshacer los cambios
          </button>
        </div>
      )}
    </form>
  );
}
